import React, { useState, useEffect } from 'react';
import { ownerPickupsApi } from '../../services/api';
import { fmt } from '../../utils/billing';

export default function DrawerAuditPage({ hotel }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState('');

  // Reload only when the hotel or date filter actually changes; `load` is
  // redefined every render and isn't memoized, so depending on it would refetch
  // on every unrelated render.
  useEffect(() => {
    if (hotel) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id, filterDate]);

  async function load() {
    try {
      setLoading(true);
      const rows = await ownerPickupsApi.list(hotel.id, filterDate ? { date: filterDate } : {});
      setLogs(rows || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="panel animate-in">
      <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="panel-title">🧾 Drawer Audit History</span>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>FILTER BY DATE:</span>
          <input 
            type="date" 
            className="input" 
            style={{ width: 140, padding: '4px 8px', fontSize: 13 }}
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />
          {filterDate && (
            <button className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => setFilterDate('')}>Clear</button>
          )}
        </div>
      </div>

      <div style={{ padding: 24 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0', color: 'var(--muted)' }}>
            <div className="spin" style={{ marginBottom: 16 }}>🌀</div>
            <div>Loading audit records...</div>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '100px 0', background: 'var(--s1)', borderRadius: 12, color: 'var(--muted)' }}>
             <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
             <div style={{ fontWeight: 700 }}>No owner pickup records found for {filterDate ? 'this date' : 'all time'}.</div>
             <div style={{ fontSize: 12, marginTop: 8 }}>New pickup entries for Nagaraju and Govind will appear here automatically.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Pickup Time</th>
                  <th>Taken By</th>
                  <th>Recorded By</th>
                  <th style={{ textAlign: 'right' }}>Cash Before</th>
                  <th style={{ textAlign: 'right' }}>Amount Taken</th>
                  <th style={{ textAlign: 'right' }}>Cash After</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{new Date(l.taken_at || l.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(l.taken_at || l.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{l.taken_by}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>Owner Pickup</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{l.recorded_by_name || l.recorded_by || '—'}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>Staff / admin entry</div>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(l.cash_before || 0)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--danger)' }}>
                      {fmt(l.amount || 0)}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 900, color: 'var(--gold)' }}>{fmt(l.cash_after || 0)}</td>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>{l.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      <div style={{ padding: '0 24px 24px', fontSize: 11, color: 'var(--muted2)', lineHeight: 1.5 }}>
        <p>⚠️ <strong>Audit Policy:</strong> Every owner pickup is a permanent cash-ledger entry. Use this report to verify how much Nagaraju or Govind collected, when it was taken, and who recorded it.</p>
      </div>
    </div>
  );
}
